type LinkedProduto = {
  pro_id?: number;
  pro_nome?: string;
  pro_categoria_item?: number;
  pro_tipo?: number;
};

type LinkedConsumption = {
  personalizacao_aplicada?: Record<string, unknown> | null;
  produto_personalizado?: LinkedProduto;
  produto_base?: LinkedProduto;
};

type LinkedItem = {
  oit_id?: number | string;
  linked_context?: {
    final_product?: LinkedProduto;
    operational_product?: LinkedProduto;
  };
  open_only?: boolean;
  is_impressao_vinculada?: boolean;
  is_consumo_base?: boolean;
  produto?: {
    nome?: string;
    categoria?: number;
    pro_id?: number;
  };
  produto_personalizado_consumo?: LinkedConsumption[];
};

export interface ProductionLinkageContext {
  finalCategoryId: number | null;
  finalProductName: string | null;
  isLinkedOperationalItem: boolean;
  isOpenOnlyFlow: boolean;
  operationalProductName: string | null;
}

export interface ConsolidatedProductionItem<TItem = LinkedItem> {
  item: TItem;
  hiddenSourceItems: TItem[];
}

const OPEN_ONLY_FINAL_CATEGORIES = new Set([10, 11]);

/**
 * Categorias de PEÇA/INSUMO base (`pro_categoria_item`) — não carregam arte por si:
 * 4 = CAMISETA (peça lisa), 5 = SUBSTRATO, 6/7/8 = TINTAS.
 * Um item dessas categorias SEM nenhum consumo de personalização é um produto "cru"
 * (ex.: camiseta lisa revendida): nada a produzir no Corel. Não deve virar card no
 * fluxo comercial — fica só na modal "Detalhes".
 *
 * NÃO inclui as categorias de arte (1/2/3/9/12 = impressões) nem as de produto final
 * personalizado (10/11): impressão avulsa e personalizados permanecem como card.
 * Ver spec/08-camiseta-lisa-fora-do-card-e-tamanhos-personalizada-spec-2026-07-21.md.
 */
const NON_ART_BASE_CATEGORIES = new Set([4, 5, 6, 7, 8]);

function isBareBaseProduct(item: LinkedItem): boolean {
  const consumos = Array.isArray(item.produto_personalizado_consumo)
    ? item.produto_personalizado_consumo.length
    : 0;

  // Tem personalização/arte → é card, independente da categoria.
  if (consumos > 0) {
    return false;
  }

  const categoria = item.produto?.categoria;
  // Conservador: só esconde categoria base CONHECIDA. Desconhecida (ou ausente)
  // permanece visível — a trava de regra de salvamento barra no clique se preciso.
  return typeof categoria === 'number' && NON_ART_BASE_CATEGORIES.has(categoria);
}

function getConsumptionType(consumption: LinkedConsumption): string {
  const raw = consumption.personalizacao_aplicada?.tipo;
  return typeof raw === 'string' ? raw.toLowerCase() : '';
}

function pickPrimaryConsumption(item: LinkedItem): LinkedConsumption | undefined {
  const consumptions = Array.isArray(item.produto_personalizado_consumo)
    ? item.produto_personalizado_consumo
    : [];

  if (consumptions.length === 0) {
    return undefined;
  }

  return (
    consumptions.find((consumption) => getConsumptionType(consumption) === 'impressao_vinculada') ||
    consumptions.find((consumption) => !!consumption.produto_personalizado?.pro_nome) ||
    consumptions[0]
  );
}

function normalizeProductName(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
}

function getItemId(item: LinkedItem): string | null {
  if (typeof item.oit_id === 'number') {
    return String(item.oit_id);
  }

  if (typeof item.oit_id === 'string' && item.oit_id.trim().length > 0) {
    return item.oit_id.trim();
  }

  return null;
}

function getLinkedFinalProduct(item: LinkedItem): LinkedProduto | null {
  if (item.linked_context?.final_product) {
    return item.linked_context.final_product;
  }

  const primaryConsumption = pickPrimaryConsumption(item);
  return primaryConsumption?.produto_personalizado ?? null;
}

function getLinkedOperationalProduct(item: LinkedItem): LinkedProduto | null {
  if (item.linked_context?.operational_product) {
    return item.linked_context.operational_product;
  }

  const primaryConsumption = pickPrimaryConsumption(item);
  return primaryConsumption?.produto_base ?? null;
}

function enrichOperationalItemWithLinkedContext<TItem extends LinkedItem>(
  operationalItem: TItem,
  sourceItem: LinkedItem,
): TItem {
  const finalProduct = getLinkedFinalProduct(sourceItem);
  const operationalProduct = getLinkedOperationalProduct(sourceItem)
    ?? (operationalItem.produto
      ? {
          pro_id: operationalItem.produto.pro_id,
          pro_nome: operationalItem.produto.nome,
          pro_categoria_item: operationalItem.produto.categoria,
        }
      : undefined);

  return {
    ...operationalItem,
    open_only:
      sourceItem.open_only === true || operationalItem.open_only === true,
    linked_context: {
      final_product: finalProduct ?? undefined,
      operational_product: operationalProduct ?? undefined,
    },
    produto_personalizado_consumo:
      Array.isArray(sourceItem.produto_personalizado_consumo) &&
      sourceItem.produto_personalizado_consumo.length > 0
        ? sourceItem.produto_personalizado_consumo
        : operationalItem.produto_personalizado_consumo,
  };
}

/**
 * Roles do fluxo de vendas (comercial/externo) que ancoram no produto final
 * comercial (ex.: CAMISETA PERSONALIZADA), escondendo as impressões internas.
 * Mesma lista servida por useSalesOrders / OrderContext — NÃO é só 'vendedor':
 * 'revenda' e 'clientes_plus' também vendem o produto final e NÃO podem enxergar
 * a impressão interna (pro 79), senão a busca de regra vai no pro_id errado.
 */
const COMMERCIAL_FORWARD_ROLES = new Set(['vendedor', 'revenda', 'clientes_plus']);

export function isCommercialForwardRole(role: string): boolean {
  return COMMERCIAL_FORWARD_ROLES.has(role);
}

export function getPedidoVisibleItems<TItem extends LinkedItem>(items: TItem[], role: string): TItem[] {
  // producao ancora no item operacional (impressão); os roles de venda ancoram no
  // item comercial (produto final).
  if (role === 'producao') {
    return consolidateProductionLinkedItems(items).map((entry) => entry.item);
  }

  if (isCommercialForwardRole(role)) {
    return consolidateCommercialLinkedItems(items).map((entry) => entry.item);
  }

  return items;
}

export function getProductionLinkageContext(item: LinkedItem | null | undefined): ProductionLinkageContext {
  if (!item) {
    return {
      finalCategoryId: null,
      finalProductName: null,
      isLinkedOperationalItem: false,
      isOpenOnlyFlow: false,
      operationalProductName: null,
    };
  }

  const linkedFinalProduct = getLinkedFinalProduct(item);
  const linkedOperationalProduct = getLinkedOperationalProduct(item);

  if (item.linked_context || item.open_only === true) {
    return {
      finalCategoryId:
        typeof linkedFinalProduct?.pro_categoria_item === 'number'
          ? linkedFinalProduct.pro_categoria_item
          : null,
      finalProductName:
        typeof linkedFinalProduct?.pro_nome === 'string'
          ? linkedFinalProduct.pro_nome
          : null,
      isLinkedOperationalItem: item.is_impressao_vinculada === true,
      isOpenOnlyFlow:
        item.open_only === true ||
        (item.is_impressao_vinculada === true &&
          typeof linkedFinalProduct?.pro_categoria_item === 'number' &&
          OPEN_ONLY_FINAL_CATEGORIES.has(linkedFinalProduct.pro_categoria_item)),
      operationalProductName:
        typeof linkedOperationalProduct?.pro_nome === 'string'
          ? linkedOperationalProduct.pro_nome
          : (typeof item.produto?.nome === 'string' ? item.produto.nome : null),
    };
  }

  const primaryConsumption = pickPrimaryConsumption(item);
  const finalProduct = primaryConsumption?.produto_personalizado;
  const finalCategoryId =
    typeof finalProduct?.pro_categoria_item === 'number'
      ? finalProduct.pro_categoria_item
      : null;
  const finalProductName =
    typeof finalProduct?.pro_nome === 'string' && finalProduct.pro_nome.trim().length > 0
      ? finalProduct.pro_nome
      : null;
  const operationalProductName =
    typeof item.produto?.nome === 'string' && item.produto.nome.trim().length > 0
      ? item.produto.nome
      : null;
  const isLinkedOperationalItem = item.is_impressao_vinculada === true;
  const isOpenOnlyFlow =
    isLinkedOperationalItem && finalCategoryId !== null && OPEN_ONLY_FINAL_CATEGORIES.has(finalCategoryId);

  return {
    finalCategoryId,
    finalProductName,
    isLinkedOperationalItem,
    isOpenOnlyFlow,
    operationalProductName,
  };
}

/**
 * Resolve o item cujo `oit_id` deve ser usado para NOMEAR e SALVAR o arquivo.
 *
 * Quando o item selecionado é comercial (ex.: CAMISETA PERSONALIZADA) e possui
 * impressão vinculada, o arquivo deve carregar o `oit_id` do item OPERACIONAL
 * (ex.: IMPRESSÃO DTF TÊXTIL INTERNA) — espelhando a resolução feita no backend
 * em `/cdr/arquivos/salvar`. O casamento comercial↔operacional segue o mesmo
 * critério do restante do módulo: nome do produto operacional.
 *
 * - Item já operacional (`is_impressao_vinculada`) → retorna ele mesmo.
 * - Item comercial com impressão vinculada presente em `allItems` → retorna a impressão.
 * - Sem vínculo ou sem impressão correspondente na lista → retorna o próprio item
 *   (não inventa destino; o backend é a fonte da verdade e bloqueia se necessário).
 */
export function resolveSaveTargetItem<TItem extends LinkedItem>(
  selectedItem: TItem,
  allItems: TItem[],
): TItem {
  if (!selectedItem || selectedItem.is_impressao_vinculada === true) {
    return selectedItem;
  }

  const operationalName = normalizeProductName(
    getLinkedOperationalProduct(selectedItem)?.pro_nome,
  );

  if (!operationalName) {
    return selectedItem;
  }

  const operationalItem = (Array.isArray(allItems) ? allItems : []).find(
    (candidate) =>
      candidate.is_impressao_vinculada === true &&
      normalizeProductName(candidate.produto?.nome) === operationalName,
  );

  return operationalItem ?? selectedItem;
}

export function consolidateProductionLinkedItems<TItem extends LinkedItem>(items: TItem[]): ConsolidatedProductionItem<TItem>[] {
  const visibleItems: ConsolidatedProductionItem<TItem>[] = [];
  const sourceByOperationalName = new Map<string, TItem[]>();

  for (const item of items) {
    const operationalName = normalizeProductName(
      getLinkedOperationalProduct(item)?.pro_nome,
    );

    if (!operationalName) {
      continue;
    }

    const current = sourceByOperationalName.get(operationalName) ?? [];
    current.push(item);
    sourceByOperationalName.set(operationalName, current);
  }

  const consumedItemIds = new Set<string>();

  for (const item of items) {
    // Insumo base consumido por produto personalizado (ex.: tecido DRY PROTECT):
    // não é trabalho de produção — a produção executa a impressão, não o tecido.
    // Fica de fora da lista de topo; continua visível na modal "Detalhes"
    // (mesma regra do fluxo do vendedor em consolidateCommercialLinkedItems).
    if (item.is_consumo_base === true) {
      continue;
    }

    const linkage = getProductionLinkageContext(item);
    const itemId = getItemId(item);

    if (itemId && consumedItemIds.has(itemId)) {
      continue;
    }

    if (linkage.isLinkedOperationalItem) {
      const operationalName = normalizeProductName(item.produto?.nome);
      const sourceItems = operationalName
        ? (sourceByOperationalName.get(operationalName) ?? []).filter((candidate) => {
            const candidateId = getItemId(candidate);
            return candidateId !== itemId;
          })
        : [];
      const sourceItem = sourceItems[0];
      const enrichedItem = sourceItem
        ? enrichOperationalItemWithLinkedContext(item, sourceItem)
        : item;

      visibleItems.push({
        item: enrichedItem,
        hiddenSourceItems: sourceItem ? [sourceItem] : [],
      });

      if (itemId) {
        consumedItemIds.add(itemId);
      }

      if (sourceItem) {
        const sourceItemId = getItemId(sourceItem);
        if (sourceItemId) {
          consumedItemIds.add(sourceItemId);
        }
      }

      continue;
    }

    const operationalName = normalizeProductName(
      getLinkedOperationalProduct(item)?.pro_nome,
    );

    if (operationalName) {
      if (itemId) {
        consumedItemIds.add(itemId);
      }

      continue;
    }

    visibleItems.push({ item, hiddenSourceItems: [] });
    if (itemId) {
      consumedItemIds.add(itemId);
    }
  }

  return visibleItems;
}

/**
 * Consolidação COMMERCIAL-FORWARD (fluxo do vendedor).
 *
 * Ancora a lista no item comercial (produto final, ex.: CAMISETA PERSONALIZADA)
 * e esconde as impressões vinculadas que ele consome — elas permanecem
 * visíveis apenas na modal "Detalhes" (que lê `__originalItems`).
 *
 * Simétrica a `consolidateProductionLinkedItems` (que ancora no operacional).
 * Ver spec/02-TEMA-2026-07-05.md.
 */
export function consolidateCommercialLinkedItems<TItem extends LinkedItem>(items: TItem[]): ConsolidatedProductionItem<TItem>[] {
  // Impressões vinculadas indexadas pelo nome do próprio produto
  const operationalByName = new Map<string, TItem[]>();
  for (const item of items) {
    if (item.is_impressao_vinculada !== true) {
      continue;
    }
    const name = normalizeProductName(item.produto?.nome);
    if (!name) {
      continue;
    }
    const bucket = operationalByName.get(name) ?? [];
    bucket.push(item);
    operationalByName.set(name, bucket);
  }

  // IDs das impressões consumidas por algum item comercial vinculado
  const consumedIds = new Set<string>();
  const hiddenByCommercialId = new Map<string, TItem[]>();
  for (const item of items) {
    if (item.is_impressao_vinculada === true) {
      continue;
    }
    const operationalName = normalizeProductName(getLinkedOperationalProduct(item)?.pro_nome);
    if (!operationalName) {
      continue;
    }
    const hidden = (operationalByName.get(operationalName) ?? [])
      .filter((candidate) => {
        const candidateId = getItemId(candidate);
        return candidateId !== null && !consumedIds.has(candidateId);
      })
      .slice(0, 1); // 1:1 — cada comercial consome no máximo uma impressão (simétrico à função de produção)
    for (const hiddenItem of hidden) {
      const hiddenId = getItemId(hiddenItem);
      if (hiddenId) {
        consumedIds.add(hiddenId);
      }
    }
    const commercialId = getItemId(item);
    if (commercialId) {
      hiddenByCommercialId.set(commercialId, hidden);
    }
  }

  // Projeção final preservando a ordem original da lista
  const visibleItems: ConsolidatedProductionItem<TItem>[] = [];
  for (const item of items) {
    const itemId = getItemId(item);

    // 🆕 Insumo base consumido por produto personalizado (ex.: tecido DRY PROTECT):
    // não tem arte, então não é card de trabalho no fluxo do vendedor. Fica de fora
    // da lista de topo; continua visível na modal "Detalhes" (que lê a lista original).
    if (item.is_consumo_base === true) {
      continue;
    }

    if (item.is_impressao_vinculada === true) {
      if (itemId && consumedIds.has(itemId)) {
        continue; // impressão consumida fica visível apenas na modal Detalhes
      }
      visibleItems.push({ item, hiddenSourceItems: [] });
      continue;
    }

    // 🆕 Peça base sem arte (ex.: CAMISETA lisa cat 4, sem consumo): não há o que
    // fazer no Corel. Sai da lista de topo; segue visível na modal "Detalhes"
    // (que lê `__originalItems`). Ver Achado 2 da spec 08.
    if (isBareBaseProduct(item)) {
      continue;
    }

    visibleItems.push({
      item,
      hiddenSourceItems: itemId ? (hiddenByCommercialId.get(itemId) ?? []) : [],
    });
  }

  return visibleItems;
}
